import React from 'react';
import { Link } from 'react-router-dom';
import SectionMessage from '@atlaskit/section-message';
import { SloDTO } from '@atlassiansox/phobos-client-ts';

import { useMiniSLOGraphController } from '@/controllers';
import { HeroNumber, Item } from '@/components/primitive';
import { positionRelative } from '@/styles';
import {
  ErrorBudgetNumber,
  SLINumber,
  SLONumber,
  LatestData,
} from '@/components/composed/slo-card/number';

import SLOCardHeader from '../header';
import {
  formatQualityScoreResult,
  getHeroNumberColorStyles,
} from '../../new-slo-views/common/utils';

import * as styles from './mini-styles';

type MiniSLOCardProps = SloDTO & {
  capabilityId?: string;
  isSkeleton?: boolean;
  isCompact?: boolean;
  isExtending?: boolean;
};

const MiniSLOCard: React.FC<MiniSLOCardProps> = ({
  name,
  publicId,
  type,
  objective,
  performance,
  indicator,
  isDraft,
  isSkeleton,
  isCompact,
  isExtending,
  quality,
  isSnapshot,
}) => {
  const { error, warnings } = useMiniSLOGraphController(
    objective,
    indicator,
    performance,
    publicId,
    quality,
    type
  );

  const isSLOQualityAvailable = !!(
    quality &&
    quality.resultItemsCount &&
    quality.resultItemsCount > 0
  );

  return (
    <div
      css={[styles.root, positionRelative, isExtending ? styles.extending : '']}
      data-testid={isSkeleton ? 'mini-slo-card-skeleton' : 'mini-slo-card'}
    >
      <div css={[styles.header]}>
        <SLOCardHeader
          shouldTruncate={!isExtending}
          publicId={publicId}
          performance={performance}
          objective={objective}
          quality={quality}
          indicator={indicator}
          isDraft={isDraft}
          isAlertable={indicator && indicator.isAlertable}
          isSkeleton={isSkeleton}
          name={name}
          type={type}
          isCompact={isCompact}
          isSnapshot={isSnapshot}
        />
      </div>
      {error && !(warnings.length > 0) ? (
        <div css={[styles.error]}>
          <SectionMessage appearance="warning" testId="mini-slo-graph-error" title={error.title}>
            <Link relative="path" to={`/slo/${publicId}`}>
              See more details
            </Link>
          </SectionMessage>
        </div>
      ) : (
        <>
          <Item title="SLI">
            <SLINumber performance={performance} objective={objective} />
          </Item>
          {isExtending && (
            <Item title="SLO - Target">
              <SLONumber isSkeleton={isSkeleton} objective={objective} />
            </Item>
          )}
          <Item title="Error Budget">
            <ErrorBudgetNumber
              performance={performance}
              objective={objective}
              isSkeleton={isSkeleton}
            />
          </Item>
          <Item title="quality score">
            <HeroNumber
              isFraction
              isSkeleton={isSkeleton}
              isUnitHidden={!isSLOQualityAvailable}
              css={getHeroNumberColorStyles({ quality, objective })}
            >
              {formatQualityScoreResult(quality?.qualityScore)}
            </HeroNumber>
          </Item>
          {isExtending && <LatestData performance={performance} objective={objective} />}
        </>
      )}
    </div>
  );
};

export default MiniSLOCard;
